/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {dlsColor, dlsShadow, dlsZIndex} from '@ion/dls/src/theme/theme';

export interface ClearNotificationDialogStyleProps {
  isPhone?: boolean;
};

// this dialog's own current-theme Notification Drawer (see
// NotificationDrawer.tsx's `currentDrawerPaper`) is anchored left and
// 456px wide, not full-viewport - MUI's Dialog centers its paper against
// the whole viewport by default, so (456 - dialog width) / 2 shifts that
// centering to land over the drawer instead ...
const DRAWER_WIDTH = 456;
const DIALOG_WIDTH = 318;
const DRAWER_CENTER_OFFSET = `${(DRAWER_WIDTH - DIALOG_WIDTH) / 2}px`;

/**
 * Returns the Dialog paper/title/content/actions style rules shared by
 * every clear-notifications confirmation dialog (`ClearNotificationsDialog`,
 * `ClearSelectedNotificationsDialog`) - each one renders the same chrome
 * around its own message body, keyed off the same drawer-centering math.
 *
 * ```typescript
 * import {clearNotificationDialogBaseStyles} from '@ion/dls/src/notifications/ClearNotificationDialogStyles';
 * ...
 *
 * const useStyles = makeStyles(() => ({
 *   ...clearNotificationDialogBaseStyles(),
 *   clearText : {
 *     color : dlsColor('grey1')
 *   }
 * }));
 * ```
 *
 * @returns The shared dialog style rules.
 */
export const clearNotificationDialogBaseStyles = () => ({
  userNotificationRoot : {
    zIndex : `${dlsZIndex('clearNotificationDialogRoot')} !important` as any
  },
  // overrides Dialog's own `scrollPaper` container (the flex box that
  // centers `paper`) so it centers over the left-anchored drawer instead
  // of the full viewport - on phone the drawer is full-width (anchor
  // 'bottom'), so the default full-viewport centering is already correct
  // there and this offset is skipped ...
  scrollPaper : {
    justifyContent : ({isPhone}: ClearNotificationDialogStyleProps) => isPhone ? 'center' : 'flex-start',
    paddingLeft    : ({isPhone}: ClearNotificationDialogStyleProps) => isPhone ? 0 : DRAWER_CENTER_OFFSET
  },
  paper : {
    opacity         : 1,
    maxWidth        : `${DIALOG_WIDTH}px`,
    borderTop       : `2px solid ${dlsColor('pastelTeal')}`,
    boxShadow       : dlsShadow(2.1),
    borderRadius    : '12px',
    backgroundColor : `${dlsColor('white')} !important`
  },
  title : {
    color         : dlsColor('grey1'),
    display       : 'flex',
    padding       : '16px 16px !important',
    fontSize      : '16px',
    position      : 'relative',
    alignItems    : 'center',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    lineHeight    : '20px',
    letterSpacing : '0px'
  },
  titleText : {
    flex         : 1,
    minWidth     : 0,
    paddingRight : '48px'
  },
  content : {
    color         : dlsColor('grey1'),
    padding       : '8px 16px 8px 16px',
    fontSize      : '14px',
    fontFamily    : 'Inter',
    fontWeight    : 400,
    lineHeight    : '20px',
    letterSpacing : '0px'
  },
  contentText : {
    fontSize      : 14,
    fontFamily    : 'Inter',
    marginBottom  : '0 !important',
    letterSpacing : '0 !important'
  },
  currentCloseButton : {
    top      : '7px',
    right    : '19px',
    position : 'absolute'
  },
  actions : {
    padding        : '8px !important',
    justifyContent : 'flex-end'
  }
});
